import {
  Box,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  HStack,
  Heading,
  VStack,
  // eslint-disable-next-line no-restricted-imports
  Button as ChakraButton,
} from '@chakra-ui/react';
import { Icon } from '../../../../../components/atoms/Icon';
import { PricingSection } from '../../organisms/Pricing';
import {
  pricingPlans,
  planDetails,
  PricingPlansType,
  PlanDetailsType,
} from './pricings';
import { useContactModal } from 'src/hooks/useContactModal';
import { ContactModal } from 'src/components/molecules/ContactModal';

const PricingPage = () => {
  const planNames = Object.keys(pricingPlans) as Array<keyof PricingPlansType>;

  const { onOpen, modalProps } = useContactModal({
    defaultInterest: 'pricing',
    title: 'Contact Us',
    submitButtonText: 'Contact',
  });

  return (
    <>
      <PricingSection showAllFeatures={false} />
      <Box pb={{ base: 8, md: 16 }} px={{ base: 4, md: 8 }}>
        <Container maxW={{ base: 'container.xl', lg: '8xl' }}>
          <Box overflowX="auto" bg="white">
            <Table variant="unstyled">
              <Thead>
                <Tr>
                  <Th width="24%"></Th>
                  {planNames.map((planName) => (
                    <Th
                      key={planName}
                      textAlign="center"
                      fontSize={{ base: 'sm', md: 'md' }}
                      fontWeight="bold"
                      color="text.darkBlue.900"
                      fontFamily="marketingPage"
                      py={6}>
                      {planName}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {/* Plan Details Section */}
                {planDetails.map((detail) => (
                  <Tr
                    key={detail.key}
                    borderTop="1px solid"
                    borderColor="gray.200">
                    <Td py={6} pl={6}>
                      <HStack spacing={3} alignItems="center">
                        <Icon
                          iconName={detail.icon}
                          w={5}
                          h={5}
                          color="icon.green"
                        />
                        <Text
                          fontWeight="bold"
                          color="text.darkBlue.900"
                          fontFamily="marketingPage">
                          {detail.label}
                        </Text>
                      </HStack>
                    </Td>
                    {planNames.map((planName) => (
                      <Td key={planName} textAlign="center" py={6}>
                        <Text
                          fontSize={{ base: 'sm', md: 'md' }}
                          color="text.darkBlue.800"
                          fontWeight="normal"
                          fontFamily="marketingPage">
                          {
                            pricingPlans[planName][
                              detail.key as keyof Omit<
                                PlanDetailsType,
                                'coreInclusions' | 'fullFeatures'
                              >
                            ]
                          }
                        </Text>
                      </Td>
                    ))}
                  </Tr>
                ))}

                {/* Core Inclusions Header */}
                <Tr>
                  <Td colSpan={4} py={6} pl={6} borderColor="gray.300">
                    <Text
                      fontWeight="bold"
                      fontSize="lg"
                      color="text.darkBlue.900"
                      fontFamily="marketingPage">
                      Core Inclusions
                    </Text>
                  </Td>
                </Tr>

                {/* Core Inclusions */}
                {Object.entries(pricingPlans[planNames[0]].coreInclusions).map(
                  ([feature]) => (
                    <Tr
                      key={feature}
                      borderTop="1px solid"
                      borderColor="gray.200">
                      <Td py={6} pl={6}>
                        <Text
                          color="text.darkBlue.800"
                          fontFamily="marketingPage">
                          {feature}
                        </Text>
                      </Td>
                      {planNames.map((planName) => {
                        const hasFeature =
                          pricingPlans[planName].coreInclusions[feature];
                        return (
                          <Td key={planName} textAlign="center" py={6}>
                            {hasFeature ? (
                              <Icon iconName="check" color="icon.green" />
                            ) : (
                              <Icon
                                iconName="close"
                                color="text.darkBlue.800"
                              />
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  ),
                )}

                {/* Full Feature List Header */}
                <Tr>
                  <Td colSpan={4} pl={6} py={6} borderColor="gray.300">
                    <Text
                      fontWeight="bold"
                      fontSize="lg"
                      color="text.darkBlue.900"
                      fontFamily="marketingPage">
                      Full Feature List
                    </Text>
                  </Td>
                </Tr>

                {/* Full Features */}
                {Object.entries(pricingPlans[planNames[0]].fullFeatures).map(
                  ([feature]) => (
                    <Tr
                      key={feature}
                      borderTop="1px solid"
                      borderColor="gray.200">
                      <Td py={6} pl={6}>
                        <Text
                          color="text.darkBlue.800"
                          fontFamily="marketingPage">
                          {feature}
                        </Text>
                      </Td>
                      {planNames.map((planName) => {
                        const hasFeature =
                          pricingPlans[planName].fullFeatures[feature];
                        return (
                          <Td key={planName} textAlign="center" py={4}>
                            {hasFeature ? (
                              <Icon iconName="check" color="icon.green" />
                            ) : (
                              <Icon
                                iconName="close"
                                color="text.darkBlue.800"
                              />
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  ),
                )}
                {/* Add-ons */}
                <Tr>
                  <Td
                    colSpan={4}
                    pl={6}
                    py={6}
                    alignItems="center"
                    borderColor="gray.300"
                    display="flex">
                    <Icon
                      w={5}
                      h={5}
                      mr={2}
                      iconName="plus"
                      color="icon.green"
                    />
                    <Text
                      fontWeight="bold"
                      fontSize="lg"
                      color="text.darkBlue.900"
                      fontFamily="marketingPage">
                      Add-ons{' '}
                    </Text>
                  </Td>
                </Tr>

                {pricingPlans[planNames[0]].addOns.map((feature, index) => (
                  <Tr
                    key={feature}
                    borderTop="1px solid"
                    borderColor="gray.200">
                    <Td py={6} pl={6} />
                    {planNames.map((planName) => {
                      const addon = pricingPlans[planName].addOns[index];
                      return (
                        <Td key={planName} textAlign="center" py={6}>
                          <Text
                            fontSize={{ base: 'sm', md: 'md' }}
                            color="text.darkBlue.800"
                            fontWeight="normal"
                            fontFamily="marketingPage">
                            {addon}
                          </Text>
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Container>
      </Box>
      <VStack spacing={4} px={4} textAlign="center" mb={16}>
        <Heading
          color={'text.darkBlue.900'}
          size="xl"
          fontFamily="marketingPage">
          Need Something Custom?
        </Heading>
        <Text fontSize="xl" color="text.darkBlue.800">
          If your media mix spans multiple countries, brands, or specialized
          datasets, we’ll build <br /> a tailored plan around your needs.
          Contact us to learn more about our <br />
          <Text as="span" fontWeight="bold" color="text.darkBlue.800">
            Enterprise Custom
          </Text>{' '}
          solutions.
        </Text>
        <ChakraButton
          onClick={onOpen}
          size="lg"
          bg={'icon.green'}
          color="white"
          fontWeight="bold"
          fontFamily="marketingPage"
          borderRadius="8px"
          mt={8}
          px={8}
          _hover={{ bg: 'icon.green.500' }}>
          Contact
        </ChakraButton>
      </VStack>
      <ContactModal {...modalProps} />
    </>
  );
};

export default PricingPage;
